import { useState } from "react";
import { Plus, Layers, Pencil, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTables, RestaurantTable } from "@/hooks/useTables";
import { BatchTableDialog } from "./BatchTableDialog";
import { toast } from "sonner";

export function TableManagementCard() {
  const { tables, isLoading, createTable, createBatchTables, updateTable, deleteTable } = useTables();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [number, setNumber] = useState("");
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("4");
  const [serviceTaxEnabled, setServiceTaxEnabled] = useState(true);

  const resetForm = () => {
    setNumber("");
    setName("");
    setCapacity("4");
    setServiceTaxEnabled(true);
  };

  const handleAdd = async () => {
    if (!number.trim()) {
      toast.error("Informe o número da mesa");
      return;
    }

    setIsSubmitting(true);
    const result = await createTable({
      number: number.trim(),
      name: name.trim() || undefined,
      capacity: parseInt(capacity) || 4,
      service_tax_enabled: serviceTaxEnabled,
    });
    setIsSubmitting(false);

    if (result) {
      resetForm();
      setShowAddDialog(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedTable || !number.trim()) return;

    setIsSubmitting(true);
    const result = await updateTable(selectedTable.id, {
      number: number.trim(),
      name: name.trim() || null,
      capacity: parseInt(capacity) || 4,
      service_tax_enabled: serviceTaxEnabled,
    });
    setIsSubmitting(false);

    if (result) {
      resetForm();
      setShowEditDialog(false);
      setSelectedTable(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedTable) return;

    setIsSubmitting(true);
    await deleteTable(selectedTable.id);
    setIsSubmitting(false);
    setShowDeleteDialog(false);
    setSelectedTable(null);
  };

  const handleToggleServiceTax = async (table: RestaurantTable) => {
    await updateTable(table.id, {
      service_tax_enabled: !table.service_tax_enabled,
    });
  };

  const openEditDialog = (table: RestaurantTable) => {
    setSelectedTable(table);
    setNumber(table.number);
    setName(table.name || "");
    setCapacity(String(table.capacity));
    setServiceTaxEnabled(table.service_tax_enabled ?? true);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (table: RestaurantTable) => {
    setSelectedTable(table);
    setShowDeleteDialog(true);
  };

  const activeTables = tables.filter(t => t.is_active);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Gerenciamento de Mesas</CardTitle>
              <CardDescription>
                Cadastre mesas individualmente ou em lote
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowBatchDialog(true)}>
                <Layers className="h-4 w-4 mr-2" />
                Em Lote
              </Button>
              <Button size="sm" onClick={() => { resetForm(); setShowAddDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Mesa
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : activeTables.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma mesa cadastrada
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="text-center">Capacidade</TableHead>
                    <TableHead className="text-center">Taxa 10%</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeTables.map((table) => (
                    <TableRow key={table.id}>
                      <TableCell className="font-medium">{table.number}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {table.name || "-"}
                      </TableCell>
                      <TableCell className="text-center">{table.capacity}</TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={table.service_tax_enabled ?? true}
                          onCheckedChange={() => handleToggleServiceTax(table)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(table)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(table)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Mesa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número *</Label>
              <Input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="01"
              />
            </div>
            <div className="space-y-2">
              <Label>Nome (opcional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Janela, VIP"
              />
            </div>
            <div className="space-y-2">
              <Label>Capacidade</Label>
              <Input
                type="number"
                min="1"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Aplicar Taxa de Serviço</Label>
              <Switch
                checked={serviceTaxEnabled}
                onCheckedChange={setServiceTaxEnabled}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Criar Mesa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Mesa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número *</Label>
              <Input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Nome (opcional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Janela, VIP"
              />
            </div>
            <div className="space-y-2">
              <Label>Capacidade</Label>
              <Input
                type="number"
                min="1"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Aplicar Taxa de Serviço</Label>
              <Switch
                checked={serviceTaxEnabled}
                onCheckedChange={setServiceTaxEnabled}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Mesa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover a mesa {selectedTable?.number}? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Dialog */}
      <BatchTableDialog
        open={showBatchDialog}
        onOpenChange={setShowBatchDialog}
        onSubmit={createBatchTables}
      />
    </>
  );
}
